<script lang="ts">
  import { Pages } from '@packages/utils/enums'
  import { onDestroy } from 'svelte'
  import { get } from 'svelte/store'
  
  import { pageOptsStore, userStore, pageStore, clientPublicStore } from '@packages/stores'
  import { dispatch, PassportEvents } from '@lib/events/events'
  import { Divider, Payment } from '@packages/ui'
  import Stripe from './stripe/Stripe.svelte'
  import Paystack from './paystack/Paystack.svelte'
  import CredenzaBalance from './credenza-balance/CredenzaBalance.svelte'
  import Loader from '@packages/ui/main/shared/Loader.svelte'

  import type { TSuiPaymentItem } from '@app/types'
  import { toastAlert } from '@src/passport/ui'
  import type { TFiatCurrency, TPaymentProvider } from './payment.types'
  import { calculateTotalPrice, getClientPaymentOptions, getCurrencyTotal } from './payment.helpers'
  import { AssetType } from './payment.constants'
  import type { TSubscriptionPurchasePageOpts } from '@packages/utils/types'
  import { createOrderStripeFlow, createSubscriptionStripeFlow } from './stripe/payment-flows'

  let isPriceLoading = false
  let isLoading = false
  let pricesByAsset: number[] = []

  const idempotencyKey = crypto.randomUUID()

  let totalSumCentsByCurrency: Partial<Record<TFiatCurrency, number>> = {}
  let totalSumCoins = 0
  let feeToApply = 0

  const clientCredFee = get(clientPublicStore).client_payment_configuration?.token_fee

  const paymentComponents = {
    stripe: Stripe,
    paystack: Paystack,
  } satisfies Record<TPaymentProvider, typeof Stripe | typeof Paystack>

  const isSubscriptionPayment = (store: TSubscriptionPurchasePageOpts) =>
    store.paymentMode === 'subscription' || !!store.subscription

  const getSubscriptionAssets = (store: TSubscriptionPurchasePageOpts) => {
    const packageId = store.subscription?.membershipResourceId

    if (!packageId) return []

    return [
      {
        packageId,
        type: AssetType.MEMBERSHIP,
        assetId: store.subscription?.tokenId,
      },
    ]
  }

  const loadTotalPrice = async (assets: TSuiPaymentItem[]): Promise<void> => {
    try {
      isPriceLoading = true

      const result = await calculateTotalPrice(assets, clientCredFee)
      console.log('result', result)

      totalSumCentsByCurrency = result.totalSumCentsByCurrency
      totalSumCoins = result.totalSumCoins
      pricesByAsset = result.pricesByAsset
      feeToApply = result.feeToApply
    } catch (err) {
      console.log(err)
      toastAlert('Error calculating the price', 'failure')
      dispatch(PassportEvents.ERROR, err)
    } finally {
      isPriceLoading = false
    }
  }

  const unsub = pageOptsStore.subscribe(async (store) => {
    const assets = isSubscriptionPayment(store) ? getSubscriptionAssets(store) : store.assets

    if (!assets || !assets.length) return

    await loadTotalPrice(assets)
  })

  onDestroy(unsub)
</script>

{#if isLoading}
  {#await import('@components/progress/ProgressVideo.svelte') then module}
    <svelte:component this={module.default} />
  {/await}
{/if}

<Payment
  bind:isLoading
  options={{
    user: $userStore,
    title: $pageOptsStore.title || `Order Summary`,
    subtitle: $pageOptsStore.subtitle,
    onBackClick: () => pageStore.set(Pages.WALLET),
    isCardDisabled: $pageOptsStore.payments?.card?.disabled,
    isCredezaValueDisabled:
      isSubscriptionPayment($pageOptsStore) ||
      !getCurrencyTotal(totalSumCentsByCurrency, 'USD') ||
      $pageOptsStore.payments?.credenzaStoredValue?.disabled,
    clientName: $clientPublicStore.name
  }}
>
  <div slot="credit-card">
    {#if isPriceLoading}
      <Loader />
    {:else}
      {#if isSubscriptionPayment($pageOptsStore)}
        {@const priceCents = getCurrencyTotal(totalSumCentsByCurrency, 'USD')}
        <Stripe
            {priceCents}
            {isPriceLoading}
            bind:isLoading
            paymentFlow={createSubscriptionStripeFlow(idempotencyKey)}
          />
      {:else}
        {#each getClientPaymentOptions() as paymentOption, index (`${paymentOption.provider}-${paymentOption.currency}`)}
          {@const priceCents = getCurrencyTotal(totalSumCentsByCurrency, paymentOption.currency)}
          {@const PaymentComponent = paymentComponents[paymentOption.provider]}

          {#if priceCents}
            {#if index > 0}
              <Divider />
            {/if}

            <svelte:component
              this={PaymentComponent}
              {priceCents}
              {isPriceLoading}
              purchaseConfig={$pageOptsStore}
              {idempotencyKey}
              bind:isLoading
              {feeToApply}
              paymentFlow={createOrderStripeFlow(idempotencyKey)}
            />
          {/if} 
        {/each}
      {/if}
    {/if}
  </div>

  <div slot="cred-balance">
    <CredenzaBalance
      {pricesByAsset}
      totalPriceToken={totalSumCoins}
      {isPriceLoading}
      purchaseConfig={$pageOptsStore}
      bind:isLoading
    />
  </div>
</Payment>
